import { faParseIconName } from '@/Components/inputs/IconAutocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';

type Props = {
  className?: string;
  iconName?: string | null | undefined;
  name?: string | null;
  url: string;
  idx?: number;
  defaultIconPkg?: 'fab' | 'fas';
  onClick?: (e: React.MouseEvent<Element>) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
};

export const IconButtonLink = ({
  className = '',
  iconName,
  name = '',
  url,
  idx = 0,
  defaultIconPkg,
  onClick,
  ...props
}: Props) => {
  const icon = faParseIconName(iconName, defaultIconPkg);

  const link = useMemo(() => {
    try {
      return new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch (e) {
      return null;
    }
  }, [url]);

  const isExternalLink = useMemo(() => {
    if (url?.startsWith('mailto:')) return true;
    if (link?.hostname !== window.location.hostname) return true;
    return false;
  }, [url]);

  if (isExternalLink) {
    return (
      <a
        aria-disabled={props?.disabled}
        style={props?.style}
        key={`${name}_${idx}`}
        className={className}
        href={url?.startsWith('mailto:') ? url : link?.href}
        onClick={props?.disabled ? (e) => e.preventDefault() : onClick}
        target={'_blank'}
        rel={'noreferrer'}
      >
        {!!icon ? <FontAwesomeIcon className="icon" icon={[icon.pkg, icon.label]} /> : null}
        <Typography variant="body1" component={'p'} className="text">
          {name}
        </Typography>
      </a>
    );
  }

  return (
    <a
      style={props?.style}
      key={`${name}_${idx}`}
      className={className}
      href={`${link?.pathname}${link?.search}`}
      onClick={(e) => {
        if (props?.disabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    >
      {!!icon ? <FontAwesomeIcon className="icon" icon={[icon.pkg, icon.label]} /> : null}{' '}
      <Typography variant="body1" component={'p'} className="text">
        {name}
      </Typography>
    </a>
  );
};

export default IconButtonLink;
